import { Application, Container, DisplayObject, Graphics, Point, Rectangle, Text } from "pixi.js"
import { EmberRosterRole, Entity } from "game-common/models"
import { ClientGameLogic } from "../../../../client_game_logic"
import { TextRenderer } from "../text/text_renderer"
import { ROLE_EMOJI } from "./ember_roster_card"
import {
    clampToRectEdge,
    EdgePlacement,
    isOffscreen,
    normalizeRect,
    placeInwardBox,
    placeInwardOfBox,
    Rect,
    StagePoint,
} from "./faction_tracker_geometry"

interface Marker {
    container: Container
    arrow: Graphics
    // Expanding "ping" drawn behind the arrow while this member is the highlighted roster card.
    // Fake glow (the same triangle stacked translucently), not a filter — see buildGlow.
    glow: Graphics
    // Role glyph (👑/🛡️/🪓, or a neutral dot for a tracked member with no roster role). Shown
    // instead of the name; the full name appears in a black panel on hover.
    glyph: Text
    role: EmberRosterRole | null
    // Hover is tracked as a count over the arrow AND the glyph (two adjacent hit targets), so
    // sliding the pointer from one to the other — where `pointerout` on one fires alongside
    // `pointerover` on the other — can't momentarily drop the tooltip.
    hoverCount: number
    // True while this member's roster card is the highlighted (follow-cam) one: the arrow turns
    // orange, breathes, and pings.
    highlighted: boolean
    // Black high-contrast panel shown inward of the glyph: the message while the member is
    // speaking, or just the name while the arrow or glyph is hovered.
    panel?: { container: Container; bg: Graphics; nameText: Text }
}

// Screen-space HUD overlay that draws arrows gliding along the screen edges toward offscreen
// members of the tracked faction. Each arrow carries the member's role glyph (👑/🛡️/🪓);
// hovering the arrow or glyph reveals the full name in a black high-contrast panel, and a
// speaking member's line shows in the same panel. Reads all state from ClientGameLogic each frame; the
// player toggles it and the server designates the faction.
//
// Coordinate handling: everything is computed in this overlay container's LOCAL space.
// The visible screen rectangle and each entity's position are obtained via PIXI transforms
// (toLocal), so nothing is assumed about the stage scale, screen size, or camera — this is
// what keeps the arrows pinned to the true screen edges.
export class FactionTracker {
    readonly container: Container
    private markers: Map<string, Marker> = new Map()
    private logic: ClientGameLogic
    private surface: Container
    private app: Application

    private readonly EDGE_MARGIN = 18
    // How far the arrow triangle can reach toward the glyph (its farthest vertex is the 9px
    // tip; near a corner the rotated arrow reaches obliquely toward the glyph, so use the tip
    // radius rather than the 6px back edge). GLYPH_GAP is the extra clear space kept between
    // the arrow and the glyph; together they set the constant near-edge clearance used by
    // placeInwardBox so the glyph never overlaps the arrow at any edge angle.
    private readonly ARROW_REACH = 9
    private readonly GLYPH_GAP = 1
    private readonly OFFSCREEN_PAD = 8
    private readonly ARROW_COLOR = 0x66ff66
    // The highlighted roster member's arrow: hot orange, breathing, with an expanding ping behind
    // it, so "where is the one I just clicked?" is answerable at a glance across a busy screen.
    private readonly HIGHLIGHT_COLOR = 0xff8a1f
    private readonly PULSE_MS = 900 // one breathe/ping cycle
    private readonly PULSE_GROW = 0.5 // the arrow swells to 1.5x at the top of the breath
    private readonly NAME_COLOR = 0x66ff66 // name line — matches the arrow/glyph
    private readonly GLYPH_SIZE = 16 // role emoji font size
    private readonly HOVER_PAD = 5 // extra hit-area padding so the small glyph is easy to hover
    private readonly PANEL_GAP = 5 // gap between the glyph and the name/message panel
    private readonly FALLBACK_GLYPH = "•" // tracked member with no roster role (e.g. a follower)

    // Rescanning every entity in the room each frame (entityManager.collect is O(n) and
    // allocates) is wasteful; the tracked-member SET changes slowly. Rebuild it a few times
    // a second and reuse the cached Entity references — their `location` is mutated in place
    // by the netcode, so arrow positions still update every frame.
    private members: Entity[] = []
    private lastScanMs = 0
    private lastScannedFaction: string | null = null
    private readonly SCAN_INTERVAL_MS = 250

    // Reused scratch points to avoid per-frame allocation.
    private readonly tmpA = new Point()
    private readonly tmpB = new Point()

    constructor(logic: ClientGameLogic, surface: Container, app: Application) {
        this.logic = logic
        this.surface = surface
        this.app = app
        this.container = new Container()
        this.container.zIndex = 50000 // above other HUD; stage.sortableChildren is on
        // Markers sort among themselves so the highlighted arrow draws over its neighbours when
        // two members are bunched at the same screen edge.
        this.container.sortableChildren = true
    }

    // Triangle pointing toward +x; the marker's rotation orients it outward. Redrawn (rather than
    // tinted) when the highlight turns on/off, so the fill is the literal colour either way.
    private drawArrow(g: Graphics, color: number) {
        g.clear()
        g.beginFill(color)
        g.moveTo(9, 0)
        g.lineTo(-6, -6)
        g.lineTo(-6, 6)
        g.closePath()
        g.endFill()
    }

    // Fake glow behind the highlighted arrow: the same triangle stacked at growing scales with
    // falling alpha, largest first. A real blur filter would cost a GPU bake per frame per arrow,
    // which this has to run without.
    private buildGlow(): Graphics {
        const g = new Graphics()
        for (const [scale, alpha] of [
            [2.6, 0.14],
            [1.9, 0.28],
            [1.35, 0.5],
        ]) {
            g.beginFill(this.HIGHLIGHT_COLOR, alpha)
            g.moveTo(9 * scale, 0)
            g.lineTo(-6 * scale, -6 * scale)
            g.lineTo(-6 * scale, 6 * scale)
            g.closePath()
            g.endFill()
        }
        g.visible = false
        return g
    }

    private buildArrow(): Graphics {
        const g = new Graphics()
        this.drawArrow(g, this.ARROW_COLOR)
        // Padded hit area around the small triangle (local bounds ~x[-6,9] y[-6,6]) so the
        // arrow itself is an easy hover target, not just the glyph. Rotates with the arrow.
        g.hitArea = new Rectangle(
            -6 - this.HOVER_PAD,
            -6 - this.HOVER_PAD,
            15 + this.HOVER_PAD * 2,
            12 + this.HOVER_PAD * 2,
        )
        return g
    }

    // Make an object a hover target that drives the marker's tooltip (arrow + glyph both use this).
    private wireHover(obj: DisplayObject, marker: Marker) {
        obj.interactive = true
        obj.cursor = "help"
        obj.on("pointerover", () => (marker.hoverCount += 1))
        obj.on("pointerout", () => (marker.hoverCount = Math.max(0, marker.hoverCount - 1)))
    }

    // The glyph text + tint for a role: the role emoji rendered in its own colors (no tint), or
    // a neutral dot tinted the arrow colour — orange while highlighted — for a tracked member with
    // no roster role.
    private glyphFor(role: EmberRosterRole | null, highlighted: boolean): { text: string; tint: number } {
        const accent = highlighted ? this.HIGHLIGHT_COLOR : this.ARROW_COLOR
        return role ? { text: ROLE_EMOJI[role], tint: 0xffffff } : { text: this.FALLBACK_GLYPH, tint: accent }
    }

    private applyGlyph(m: Marker) {
        const g = this.glyphFor(m.role, m.highlighted)
        m.glyph.text = g.text
        m.glyph.tint = g.tint
        // Padded hit area (measured after the text is set) so the small glyph is easy to hover.
        const hw = m.glyph.width / 2 + this.HOVER_PAD
        const hh = m.glyph.height / 2 + this.HOVER_PAD
        m.glyph.hitArea = new Rectangle(-hw, -hh, hw * 2, hh * 2)
    }

    private ensureMarker(entityId: string, role: EmberRosterRole | null): Marker {
        let m = this.markers.get(entityId)
        if (!m) {
            const container = new Container()
            const glow = this.buildGlow()
            const arrow = this.buildArrow()
            const glyph = new TextRenderer().fontSize(this.GLYPH_SIZE).render() as Text
            glyph.anchor.set(0.5)
            container.addChild(glow) // behind the arrow
            container.addChild(arrow)
            container.addChild(glyph)
            this.container.addChild(container)
            m = { container, glow, arrow, glyph, role, hoverCount: 0, highlighted: false }
            this.wireHover(arrow, m) // hovering the arrow OR the glyph shows the name tooltip
            this.wireHover(glyph, m)
            this.applyGlyph(m)
            this.markers.set(entityId, m)
        } else if (m.role !== role) {
            m.role = role
            this.applyGlyph(m)
        }
        return m
    }

    // Turn the "this is the roster card you have open" treatment on/off for a marker. Only the
    // state CHANGE redraws; the per-frame motion is in animateHighlight.
    private setHighlight(m: Marker, on: boolean) {
        if (m.highlighted === on) {
            return
        }
        m.highlighted = on
        this.drawArrow(m.arrow, on ? this.HIGHLIGHT_COLOR : this.ARROW_COLOR)
        this.applyGlyph(m)
        m.glow.visible = on
        m.container.zIndex = on ? 1 : 0
        if (!on) {
            m.arrow.scale.set(1)
        }
    }

    // The pulse itself: the arrow breathes (smooth cosine) while a ping expands and fades out of
    // it (sawtooth), both off one shared clock so every highlighted arrow beats in time.
    private animateHighlight(m: Marker, nowMs: number, placement: EdgePlacement) {
        const t = (nowMs % this.PULSE_MS) / this.PULSE_MS
        const breathe = 0.5 - 0.5 * Math.cos(t * Math.PI * 2)
        m.arrow.scale.set(1 + this.PULSE_GROW * breathe)
        m.glow.position.set(placement.x, placement.y)
        m.glow.rotation = placement.angle
        m.glow.scale.set(0.85 + 0.9 * t)
        m.glow.alpha = 0.85 * Math.pow(1 - t, 1.5)
    }

    private hideMarker(m: Marker) {
        m.container.visible = false
        m.hoverCount = 0 // pointerout won't fire while hidden; clear so no stale tooltip on return
    }

    private hideAll() {
        this.markers.forEach(m => this.hideMarker(m))
    }

    // Show the black high-contrast panel inward of the glyph: the name (name color), and — when
    // the member is speaking — the message (message color) stacked below it. `message` is null
    // for a plain hover (name only). Positioned just past the glyph (via placeInwardOfBox) so it
    // never covers the glyph or the arrowhead.
    // The hover label: just the member's name. What they SAY is no longer shown here — an offscreen
    // member's words go to the ember roster's speech lane instead (gui/roster_speech_lane.ts), where
    // they sit above that member's own portrait card. Showing the line in both places at once was
    // the same sentence twice on screen.
    private showPanel(
        marker: Marker,
        name: string,
        glyphCenter: StagePoint,
        glyphW: number,
        glyphH: number,
        placement: EdgePlacement,
        rect: Rect,
    ) {
        if (!marker.panel) {
            const container = new Container()
            const bg = new Graphics()
            const nameText = new TextRenderer().size("tiny").color(this.NAME_COLOR).render() as Text
            nameText.anchor.set(0.5, 0) // top-centered
            container.addChild(bg)
            container.addChild(nameText)
            marker.container.addChild(container)
            marker.panel = { container, bg, nameText }
        }
        const panel = marker.panel
        if (panel.nameText.text !== name) {
            panel.nameText.text = name
        }

        const padX = 6
        const padY = 3
        const w = panel.nameText.width + padX * 2
        const h = panel.nameText.height + padY * 2

        panel.nameText.position.set(0, -h / 2 + padY)

        panel.bg.clear()
        panel.bg.beginFill(0x000000, 0.85) // near-opaque black for high contrast over the world
        panel.bg.lineStyle(1, this.ARROW_COLOR, 0.9)
        panel.bg.drawRoundedRect(-w / 2, -h / 2, w, h, 4)
        panel.bg.endFill()

        // Sit the panel just past the glyph (inward), aligned with it, and keep it on screen.
        const pos = placeInwardOfBox(placement, glyphCenter, glyphW, glyphH, w, h, rect, this.PANEL_GAP)
        panel.container.position.set(pos.x, pos.y)
        panel.container.visible = true
    }

    private hidePanel(marker: Marker) {
        if (marker.panel) {
            marker.panel.container.visible = false
        }
    }

    private nameFor(entity: Entity): string {
        const anyEntity = entity as any
        if (typeof anyEntity.safeName === "function") {
            return anyEntity.safeName() || ""
        }
        return entity.name || ""
    }

    // The visible screen rectangle expressed in this overlay's local coordinate space,
    // derived from the renderer's actual screen corners via PIXI transforms.
    private screenRectInLocal(): Rect {
        this.tmpA.set(this.app.screen.x, this.app.screen.y)
        this.tmpB.set(this.app.screen.x + this.app.screen.width, this.app.screen.y + this.app.screen.height)
        const tl = this.container.toLocal(this.tmpA)
        const br = this.container.toLocal(this.tmpB)
        return normalizeRect(tl, br)
    }

    update() {
        const logic = this.logic
        if (!logic.trackingEnabled || !logic.trackedFaction) {
            this.hideAll()
            return
        }

        const player = logic.playerEntity
        if (!player || !player.location) {
            this.hideAll()
            return
        }

        const rect = this.screenRectInLocal()

        // Throttled rescan of the tracked-faction member set (also rescans immediately if
        // the tracked faction changed). Positions come from the cached refs each frame.
        const nowMs = performance.now()
        if (nowMs - this.lastScanMs >= this.SCAN_INTERVAL_MS || this.lastScannedFaction !== logic.trackedFaction) {
            this.lastScanMs = nowMs
            this.lastScannedFaction = logic.trackedFaction
            this.members = logic.entityManager.collect(
                (e: Entity) => e.faction === logic.trackedFaction && e.entityId !== player.entityId,
            )
        }

        // The highlighted roster card IS the follow-cam focus (EmberRosterWidget.onSlotClick ->
        // GuiManager.openFollowCam -> setFocusEntityId), so that one id is the whole signal.
        const highlightedId = logic.focusEntityId

        const present = new Set<string>()
        for (const entity of this.members) {
            // Skip stale cached refs: entity hidden, removed from the manager, or already
            // scheduled for destruction (removed next cleanup). Not adding it to `present`
            // makes the cleanup pass below destroy its arrow this frame.
            if (
                !entity.location ||
                entity.visible === false ||
                !logic.hasEntity(entity.entityId) ||
                logic.isDestroyed(entity.entityId)
            ) {
                continue
            }
            present.add(entity.entityId)

            // Entity world position -> this overlay's local space (exact, via the world
            // container's transform).
            this.tmpA.set(entity.location.x, entity.location.y)
            const p = this.container.toLocal(this.tmpA, this.surface)

            if (!isOffscreen(p, rect, this.OFFSCREEN_PAD)) {
                const existing = this.markers.get(entity.entityId)
                if (existing) {
                    this.hideMarker(existing)
                }
                continue
            }

            const placement = clampToRectEdge(p, rect, this.EDGE_MARGIN)
            const marker = this.ensureMarker(entity.entityId, this.logic.getEmberRole(entity.entityId))
            this.setHighlight(marker, !!highlightedId && entity.entityId === highlightedId)
            marker.container.visible = true
            marker.arrow.position.set(placement.x, placement.y)
            marker.arrow.rotation = placement.angle
            if (marker.highlighted) {
                this.animateHighlight(marker, nowMs, placement)
            }

            // The role glyph, offset perpendicular to the arrow's screen edge and aligned along
            // it, so it sits centered next to the arrow without overlapping the arrowhead.
            // Reserve clearance for the arrow at its LARGEST (peak of the breath) rather than
            // re-placing the glyph every frame, which would make it jitter in and out.
            const reach = marker.highlighted ? this.ARROW_REACH * (1 + this.PULSE_GROW) : this.ARROW_REACH
            const gp = placeInwardBox(placement, marker.glyph.width, marker.glyph.height, rect, reach, this.GLYPH_GAP)
            marker.glyph.position.set(gp.x, gp.y)

            // The black panel (just past the glyph) names the member while the arrow or glyph is
            // hovered. The arrow's job is DIRECTION — where they are; what they're saying belongs to
            // the roster's speech lane, which has their portrait and name already.
            if (marker.hoverCount > 0) {
                this.showPanel(
                    marker,
                    this.nameFor(entity),
                    gp,
                    marker.glyph.width,
                    marker.glyph.height,
                    placement,
                    rect,
                )
            } else {
                this.hidePanel(marker)
            }
        }

        // Drop markers whose entity is gone (left room, died, out of sync range).
        for (const [id, m] of this.markers) {
            if (!present.has(id)) {
                m.container.destroy({ children: true })
                this.markers.delete(id)
            }
        }
    }
}
